import { Component, HostListener, OnInit, QueryList, ViewChildren } from '@angular/core';
import { APP_CONFIG } from '@core-app/app.config';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { Observable } from 'rxjs';
import { HeaderComponent } from '../../header/header.component';

@Component({
    selector: 'core-top-navigation',
    templateUrl: './top-nav.component.html',
    standalone: false,
})
export class TopNavComponent extends HeaderComponent implements OnInit {
    @ViewChildren(BsDropdownDirective)
    dropdowns?: QueryList<BsDropdownDirective>;

    pillars$!: Observable<any>;

    resizeTimeout: any;
    clickIndexNo?: void | number;
    navigationOrder: 'name' | 'displayOrder' = 'name';
    menuStyle = (APP_CONFIG as any).navigation?.style || 'default';

    // Menu variables
    content$?: Observable<any>;
    public openDropdown: any;
    isDesktop: boolean = true;
    isCategoryNav?: boolean;
    isMobileNavFooter: boolean = true; // Boolean to use a fixed footer element for cta at bottom of mobile nav
    contactEnabled!: boolean;
    dropdownIsOpen!: boolean;

    get isNavbarWithFlyout(): boolean | undefined {
        return (this.menuStyle === 'flyout' && !this.isIndividualMenuFlyout) || this.isIndividualMenuFlyout;
    }

    @HostListener('window:resize', ['$event']) onResize(event: Event) {
        if (this.isBrowser) {
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = setTimeout(() => {
                this.isDesktopCheck(event.target);
            }, 200);
        }
    }

    // Close dropdown menu on escape click
    @HostListener('keydown.esc')
    onEsc() {
        if (this.dropdownIsOpen && this.dropdowns) {
            this.dropdowns.forEach((dropdown: BsDropdownDirective) => {
                // dropdown from BsDropdownDirective is private so assigning it to public var to allow us to use _elementRef below
                this.openDropdown = dropdown;
                // Check if current iteration is our opened one
                if (this.openDropdown._elementRef.nativeElement.classList.contains('show')) {
                    // Add focus manually back on navbar link
                    this.openDropdown._elementRef.nativeElement.firstChild.focus();
                }
                this.openDropdown.hide();
            });
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.push(
            this.settings.showBenefitCategories().subscribe((state: any) => {
                this.isCategoryNav = state;
            }),
        );

        if (this.isBrowser) {
            this.isDesktopCheck(window);
        }

        this.subscriptions.push(
            this.settings.isContactEnabled().subscribe((state: any) => {
                this.contactEnabled = state;
            }),
        );
        this.navigationOrder = (APP_CONFIG as any).navigation['navigationOrder'] ?? 'name';
    }

    onOpenChange(isOpen: boolean, index: number): void {
        const overlayElem = this.documentService.querySelector('.page-overlay');

        if (isOpen && overlayElem) {
            this.renderer.addClass(overlayElem, 'page-overlay--is-active');
            this.clickIndexNo = index;
        } else if (this.clickIndexNo === index) {
            // only close page overlay when the same link is clicked twice
            this.renderer.removeClass(overlayElem, 'page-overlay--is-active');
        }
    }

    isDesktopCheck(elem: any) {
        if (elem.innerWidth >= 992) {
            if (!this.isCollapsed) {
                this.isCollapsed = true;
            }
            this.isDesktop = true;
        } else {
            this.isDesktop = false;
        }

        this.setMobChildNavHeight();
    }

    // This handles changing pages. It's shared across all screen sizes.
    // 1: It will automatically close the dropdown menu on Desktop
    // 2: It will also remove the mobile styling if user navigates page on mobile
    // 3: If the menu is open on mobile it will close it

    setMobChildNavHeight() {
        // 100vh fix for iOS and Android
        // We effectively calculate the left over real-estate for the scrolling sub-menu of pages.
        // Yes I know, I know...
        // Current value is 374px
        // 91px - Header bar
        // 118px - Search section (includes margin)
        // 70px - 'Back' link
        // 94px - Country Selector

        // Changing space needed at bottom of mob nav if we need it for a cta button
        const offset = this.isMobileNavFooter ? 374 : 320;
        const vh = this.windowService.innerHeight - offset;
        this.documentService.documentElement?.style.setProperty('--vhcustom-child-menu', `${vh}px`);

        // LifeMoment height fix for smaller screens
        // See above
        // 90px Header Bar
        // 67px Navigation Bar
        // 60px Padding (30px top/bottom)
        // let vhlm = window.innerHeight - 90 - 67 - 60
        // this.documentService.documentElement.style.setProperty('--vhcustom-lifemoments', `${vhlm}px`);
    }

    // 6: Will navigate to correct page
    navigateToPage(page: string) {
        if (this.dropdowns) {
            this.hideDropdowns();
            this.onOpenChange(false, -1);
        }
        if (this.documentService.body) {
            this.documentService.body.style.overflowY = 'revert';
        }
        if (!this.isCollapsed) {
            this.isCollapsed = true;
        }

        if (page && page.indexOf('#') > -1) {
            // Contains HASH!
            const urlArray = page.split('#');
            this.router.navigate([urlArray[0]], { fragment: urlArray[1] });
        } else {
            // No Hash, proceed as usual
            if (page === 'default') {
                page = '';
            }
            this.router.navigate(['/' + page]);
        }
    }

    closeChildDropdown() {
        if (this.dropdowns) {
            this.hideDropdowns();
            this.onOpenChange(false, -1);
        }
    }

    stopPropagation($event: MouseEvent | KeyboardEvent | TouchEvent | Event) {
        return $event.stopPropagation();
    }

    hideDropdowns() {
        this.dropdowns?.forEach((dropdown: BsDropdownDirective) => {
            dropdown.hide();
        });
    }

    // Method to scroll to contact form
    scrollToContactForm(): void {
        const contactElement = this.documentService.getElementById('contact-form');
        if (contactElement) {
            contactElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }
}
